import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';

import { IFilter } from '../../common/interfaces/IFilter';
import { FilterSelect } from '../dashboard/FilterSelect';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';
import {
  getPlanFCsvUrl,
  listPlanFUploads,
  PlanFUploadHistoryRow,
  uploadPlanFCsv,
} from '../plan-f-upload/api';

const PLAN_F_UPLOADS_QUERY_KEY = ['plan-f-uploads'];

const COLUMN_COUNT = 7;

// Keep in sync with carriers supported by the Plan F workflow.
const PLAN_F_CARRIERS = ['LGA', 'Ameritas', 'North American', 'Protective'] as const;

const CARRIER_FILTER_OPTIONS: IFilter[] = PLAN_F_CARRIERS.map(c => ({ value: c, label: c }));

export function PlanFPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const [carrierFilter, setCarrierFilter] = useState<IFilter[]>([]);
  const [uploadDateSort, setUploadDateSort] = useState<'desc' | 'asc'>('desc');

  const {
    data: uploads = [],
    isLoading: loadingUploads,
    error: uploadsError,
  } = useQuery({
    queryKey: PLAN_F_UPLOADS_QUERY_KEY,
    queryFn: listPlanFUploads,
  });
  const uploadRows = Array.isArray(uploads) ? uploads : [];

  const displayRows = useMemo(() => {
    let rows =
      carrierFilter.length > 0
        ? uploadRows.filter(row =>
            carrierFilter.some(f => f.value === (row.carrier || '').trim()),
          )
        : uploadRows;
    rows = [...rows].sort((a, b) => {
      const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      return uploadDateSort === 'desc' ? tb - ta : ta - tb;
    });
    return rows;
  }, [uploadRows, carrierFilter, uploadDateSort]);

  const onCarrierFilterChange = (event: SelectChangeEvent<IFilter[]>) => {
    const value = event.target.value;
    setCarrierFilter(Array.isArray(value) ? value : []);
  };

  const handleUploadDateSort = () => {
    setUploadDateSort(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const {
    mutate: uploadFile,
    isLoading: uploading,
    data: uploadResponse,
    error: uploadError,
  } = useMutation({
    mutationFn: async (files: File[]) => uploadPlanFCsv(files[0]),
    onSuccess: uploadedPlanFReport => {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.SUCCESS,
          message: 'Plan F CSV uploaded successfully',
        },
      ]);
      queryClient.setQueryData<PlanFUploadHistoryRow[]>(
        PLAN_F_UPLOADS_QUERY_KEY,
        existingUploads => [
          {
            ...uploadedPlanFReport,
            uploadedBy: uploadedPlanFReport.uploadedBy || '-',
          },
          ...(existingUploads || []),
        ],
      );
    },
    onError: (error: any) => {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: error?.message || error || 'Failed to upload Plan F CSV',
        },
      ]);
    },
  });

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      uploadFile(Array.from(files));
      e.target.value = '';
    }
  };

  const openCsv = async (upload: PlanFUploadHistoryRow, download = false) => {
    const csvWindow = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const { url } = await getPlanFCsvUrl(upload.historyId, download);
      if (csvWindow) {
        csvWindow.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      csvWindow?.close();
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: 'Failed to open Plan F CSV',
        },
      ]);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <Typography variant="h1">Plan F Upload</Typography>

        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            size="medium"
            onClick={handleUploadButtonClick}
            disabled={uploading}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Uploading...
              </Box>
            ) : (
              'Upload Plan F CSV'
            )}
          </Button>
        </Box>
      </Box>

      {uploadResponse && !uploading && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Upload successful — {uploadResponse.taskCreatedCount} task(s) created,{' '}
          {uploadResponse.flaggedRowCount} flagged, {uploadResponse.autoIgnoredRowCount} auto-ignored.
        </Alert>
      )}

      {uploadError && !uploading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {uploadError instanceof Error
            ? uploadError.message
            : 'An error occurred while uploading the file'}
        </Alert>
      )}

      {uploadsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load Plan F upload history.
        </Alert>
      )}

      {loadingUploads ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {uploadRows.length > 0 && (
            <Box display="flex" flexDirection="row" mb={2}>
              <FilterSelect
                filterName="Carrier"
                options={CARRIER_FILTER_OPTIONS}
                selected={carrierFilter}
                onChange={onCarrierFilterChange}
              />
            </Box>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>CARRIER</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active
                      direction={uploadDateSort}
                      onClick={handleUploadDateSort}
                    >
                      UPLOAD DATE
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>UPLOADED BY</TableCell>
                  <TableCell>TOTAL ROWS</TableCell>
                  <TableCell>AUTO IGNORE</TableCell>
                  <TableCell>FOR UW REVIEW</TableCell>
                  <TableCell>CSV</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map(upload => (
                  <TableRow key={upload.historyId}>
                    <TableCell>{upload.carrier || '-'}</TableCell>
                    <TableCell>
                      {upload.uploadedAt
                        ? new Date(upload.uploadedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>{upload.uploadedBy || '-'}</TableCell>
                    <TableCell>{upload.totalRows}</TableCell>
                    <TableCell>{upload.autoIgnoredRowCount}</TableCell>
                    <TableCell>{upload.flaggedRowCount}</TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => openCsv(upload, true)}
                      >
                        {upload.originalFilename}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {uploadRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Typography variant="body2" color="textSecondary">
                        No Plan F uploads yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {uploadRows.length > 0 && displayRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Typography variant="body2" color="textSecondary">
                        No uploads match the selected carrier.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
